import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ICONS as Icons } from "../../data/iconRegistry";
import { ChevronLeft, ChevronRight, Sparkles, ArrowRight, ShieldCheck, Zap, KeyRound, BadgeCheck, HeadphonesIcon, Lock, Gift, Repeat, Users, Award } from "lucide-react";
import { api } from "../../lib/api";
import { useLanguage } from "../../contexts/LanguageContext";
import { useCurrency } from "../../contexts/CurrencyContext";
import { getNetworkMeta } from "../../data/networksMeta";

const COMBO_NETWORKS = ["instagram", "facebook", "tiktok", "youtube", "twitter"];

const BENEFIT_ICONS = [Award, KeyRound, Zap, Users, HeadphonesIcon, Lock, ShieldCheck, BadgeCheck, Repeat, Gift];
const BENEFIT_KEYS = [
  ["combo.benefit_1", "Jusqu'à 16% d'économie vs l'achat à l'unité"],
  ["combo.benefit_2", "Aucun mot de passe demandé, zéro risque"],
  ["combo.benefit_3", "Livraison progressive, rendu 100% naturel"],
  ["combo.benefit_4", "Profils réels et actifs, jamais de bots"],
  ["combo.benefit_5", "Support client dédié 7j/7"],
  ["combo.benefit_6", "Paiement 100% sécurisé (CB, Apple/Google Pay)"],
  ["combo.benefit_7", "Résultats visibles en 24 à 72h"],
  ["combo.benefit_8", "Compatible avec tous les comptes, y compris pro"],
  ["combo.benefit_9", "Garantie satisfait ou remboursé"],
  ["combo.benefit_10", "Points de fidélité sur chaque commande"],
];

export default function HomeComboCarousel() {
  const { field, t } = useLanguage();
  const { format } = useCurrency();
  const [packs, setPacks] = useState([]);
  const [paused, setPaused] = useState(false);
  const scrollerRef = useRef(null);
  const BENEFITS = BENEFIT_KEYS.map(([key, fallback], i) => ({ icon: BENEFIT_ICONS[i], text: t(key, fallback) }));

  useEffect(() => {
    Promise.all(COMBO_NETWORKS.map((slug) => api.get("/products", { params: { network: slug } }).then(({ data }) => ({ slug, data })).catch(() => ({ slug, data: [] }))))
      .then((results) => {
        const list = [];
        results.forEach(({ slug, data }) => {
          data.filter((p) => p.category === "combo").sort((a, b) => a.price - b.price).forEach((p) => list.push({ ...p, network_slug: slug }));
        });
        setPacks(list);
      });
  }, []);

  const scrollByCard = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector("[data-combo-card]");
    const step = card ? card.offsetWidth + 16 : 280;
    if (dir > 0 && el.scrollLeft + el.clientWidth >= el.scrollWidth - 10) {
      el.scrollTo({ left: 0, behavior: "smooth" });
    } else {
      el.scrollBy({ left: dir * step, behavior: "smooth" });
    }
  };

  useEffect(() => {
    if (packs.length === 0 || paused) return;
    const id = setInterval(() => scrollByCard(1), 3800);
    return () => clearInterval(id);
  }, [packs.length, paused]);

  if (packs.length === 0) return null;

  return (
    <div className="w-full max-w-6xl mx-auto mb-10 relative" data-testid="home-combo-carousel" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <div className="flex items-center justify-between mb-4 px-1">
        <div className="inline-flex items-center gap-2 text-xs font-bold px-3 py-1.5 rounded-full bg-gradient-to-r from-indigo-500 to-fuchsia-500 text-white">
          <Sparkles className="w-3.5 h-3.5" /> {t("combo.badge", "Packs Combo les plus demandés")}
        </div>
        <div className="hidden sm:flex items-center gap-2">
          <button onClick={() => scrollByCard(-1)} aria-label={t("combo.prev", "Précédent")} className="w-8 h-8 rounded-full glass flex items-center justify-center text-white hover:bg-white/10 transition-colors" data-testid="combo-carousel-prev">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button onClick={() => scrollByCard(1)} aria-label={t("combo.next", "Suivant")} className="w-8 h-8 rounded-full glass flex items-center justify-center text-white hover:bg-white/10 transition-colors" data-testid="combo-carousel-next">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div ref={scrollerRef} className="flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: "none" }}>
        {packs.map((pack) => {
          const meta = getNetworkMeta(pack.network_slug);
          const Icon = Icons[meta.icon] || Icons.Globe;
          const discountPct = pack.compare_at_price ? Math.round((1 - pack.price / pack.compare_at_price) * 100) : 0;
          const services = (field(pack, "description") || "").split(" + ");
          return (
            <Link
              key={pack.id}
              to={`/reseaux/${pack.network_slug}?combo=1`}
              data-combo-card
              className="shrink-0 snap-start w-[80%] sm:w-[46%] lg:w-[31%]"
              data-testid={`combo-carousel-card-${pack.id}`}
            >
              <div className="h-full rounded-2xl p-5 border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] hover:-translate-y-1 transition-all duration-300 relative overflow-hidden group">
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: `linear-gradient(135deg, ${meta.solid}22, transparent)` }} />
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${meta.solid}33, ${meta.solid}0d)`, border: `1px solid ${meta.solid}40` }}>
                        <Icon className="w-4 h-4" style={{ color: meta.solid }} />
                      </div>
                      <div className="text-sm font-semibold text-white capitalize">{pack.network_slug === "twitter" ? "X (Twitter)" : pack.network_slug}</div>
                    </div>
                    {pack.popular && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-400 text-zinc-900">{t("combo.popular", "Populaire")}</span>}
                  </div>
                  <div className="text-xs text-zinc-500 mb-1">{field(pack, "name")}</div>
                  <div className="flex items-baseline gap-2 mb-3">
                    <span className="font-heading text-2xl font-extrabold text-white">{format(pack.price)}</span>
                    {pack.compare_at_price && (
                      <>
                        <span className="text-xs text-zinc-500 line-through">{format(pack.compare_at_price)}</span>
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400">-{discountPct}%</span>
                      </>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1 mb-4">
                    {services.slice(0, 3).map((s, i) => (
                      <span key={i} className="text-[10px] px-2 py-1 rounded-md bg-white/5 text-zinc-400 border border-white/5">{s.trim()}</span>
                    ))}
                  </div>
                  <div
                    className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full text-white w-full justify-center transition-transform group-hover:scale-[1.02]"
                    style={{ background: `linear-gradient(135deg, ${meta.solid}, #a855f7)` }}
                    data-testid={`combo-carousel-cta-${pack.id}`}
                  >
                    {t("combo.view_pack", "Voir ce pack")} <ArrowRight className="w-3.5 h-3.5" />
                  </div>
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.02] p-4">
        <p className="text-center text-xs font-semibold text-zinc-400 mb-3">{t("combo.benefits_title", "10 raisons de choisir un pack combo")}</p>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {BENEFITS.map((b, i) => (
            <div key={i} className="flex items-start gap-1.5" data-testid={`combo-benefit-${i}`}>
              <b.icon className="w-3.5 h-3.5 text-indigo-400 shrink-0 mt-0.5" />
              <span className="text-[10.5px] leading-tight text-zinc-500">{b.text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
